import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Reset, MacCommand, Save } from '@carbon/react/icons';
import { useHotkeysContext } from 'react-hotkeys-hook';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { ForecastSettings } from './forecast-settings';
import { VolumeModel } from './volume-model';
import {
  gasModelFormAtom,
  oilModelFormAtom,
  waterModelFormAtom,
  resetForecastSettingsFormAtom,
  oilModelFormValuesAtom,
  gasModelFormValuesAtom,
  waterModelFormValuesAtom,
  selectedDcaScenarioAtom,
  selectedRecordVersionAtom,
  resetSelectedDcaScenarioAtom,
  resetModelFormAtom,
  resetSelectedTypeCurveAtom,
  resetSelectedRecordVersionAtom,
  resetSelectedFitStreamsAtom,
  isWaterModelValidAtom,
  isGasModelValidAtom,
  isOilModelValidAtom,
  isModelFormDirtyAtom,
  saveDcaForecasVersionAtom,
  selectedProductionSourceAtom,
  fittedProductionAtom,
  selectedTypeCurveAtom,
  segmentationAndOutlierMarkersAtom,
  setDcaNotificationAtom,
  clearDcaNotificationGroupAtom,
  triggerRefetchInsightForecastVersionsAtom,
  totalInsightForecastVersionsAtom,
} from '../atoms';
import { STREAM_ORDER } from '../hooks';
import { HOTKEY_SCOPES } from 'lib/constants';
import {
  dcaHotkeysHelpOpenAtom,
  dcaHotkeysModeAtom,
  dcaSaveRequestAtom,
} from './hotkeys-mode/atoms';

import style from './configuration-panel.module.scss';
import {
  DcaScenarioSelector,
  DUMMY_DCA_SCENARIOS,
} from './dca-scenario-selector';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TypeCurveSelector } from './type-curve-selector';
import { Button, Tooltip } from '@carbon/react';
import { FitStreamsSelector } from './fit-streams-selector';
import { ProductionVersionSelector } from './production-version-selector';
import { MineralType } from 'types/minerals';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { Wellbore } from 'types/api-responses';
import type { WellProductionDataSources } from 'types/api-responses';
import { DcaForecast, DeclineCurveAnalysisParams } from 'types/api-payloads';
import { savedProduction } from '../production-version-utils';
import {
  ForecastVersion,
  PdpForecastVersionSelector,
} from './pdp-forecast-version-selector';
import type { DcaForecastSavePayload, DcaSaveAction } from './dca-save-action';

interface ConfigurationPanelProps {
  uwi: string;
  recordVersionHref: string;
  /** Daily production versions; without it only IHS production is offered. */
  dailyRecordVersionHref?: string;
  /** The sources the well has production for and its default, or undefined while they load. */
  productionDataSources?: WellProductionDataSources;
  legacyPdpForecastVersionsHref: string;
  insightsPdpForecastVersionsHref: string;
  earlyTimeStreams: MineralType[];
  disableFitStreams: boolean;
  /**
   * Whether hotkeys mode is available here. Only the tearsheet mounts the keys
   * that leave the mode, so anywhere else the control would arm the parameter
   * keys with no way out.
   */
  enableHotkeysMode?: boolean;
  dcaWorkspaceId?: number;
  onReset: () => void;
  onApplyPdpForecastVersion: (
    version: ForecastVersion,
    declineType?: 'PDP' | 'TC'
  ) => void;
  onSelectedPdpVersionsChange?: (versions: ForecastVersion[]) => void;
  producingWellBore: Wellbore | null;
  dcaForecast: DcaForecast | null;
  onForecastSaved?: () => void;
  /**
   * Takes over the Save button. The Well Analysis tearsheet has no workspace
   * to save into, so it makes one and promotes the forecast instead.
   */
  saveAction?: DcaSaveAction;
}

interface PanelSectionProps {
  title: string;
  children: React.ReactNode;
  onReset: () => void;
  /** Marks the section whose parameters the hotkeys are currently driving. */
  isHotkeyTarget?: boolean;
  /**
   * Controls the section's open state. Omit to let the section manage its own,
   * which is what every section but the three stream models does.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const ConfigurationPanel = ({
  uwi,
  recordVersionHref,
  dailyRecordVersionHref,
  productionDataSources,
  legacyPdpForecastVersionsHref,
  insightsPdpForecastVersionsHref,
  earlyTimeStreams,
  disableFitStreams = false,
  enableHotkeysMode = false,
  dcaWorkspaceId,
  onReset,
  onApplyPdpForecastVersion,
  onSelectedPdpVersionsChange,
  producingWellBore,
  dcaForecast,
  onForecastSaved,
  saveAction,
}: ConfigurationPanelProps) => {
  const { disableScope, toggleScope, activeScopes } = useHotkeysContext();
  const [hotkeysMode, setHotkeysMode] = useAtom(dcaHotkeysModeAtom);
  const setHelpOpen = useSetAtom(dcaHotkeysHelpOpenAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const selectedRecordVersion = useAtomValue(selectedRecordVersionAtom);
  const productionSource = useAtomValue(selectedProductionSourceAtom);
  const [fittedProduction, setFittedProduction] = useAtom(fittedProductionAtom);
  const selectedTypeCurve = useAtomValue(selectedTypeCurveAtom);
  const [saveDcaForecasVersion, setSaveDcaForecasVersion] = useAtom(
    saveDcaForecasVersionAtom
  );
  const segmentationAndOutlierMarkers = useAtomValue(
    segmentationAndOutlierMarkersAtom
  );
  const totalInsightForecastVersions = useAtomValue(
    totalInsightForecastVersionsAtom
  );
  const setDcaNotification = useSetAtom(setDcaNotificationAtom);
  const clearDcaNotificationGroup = useSetAtom(clearDcaNotificationGroupAtom);
  const triggerRefetchInsightForecastVersions = useSetAtom(
    triggerRefetchInsightForecastVersionsAtom
  );
  const [recentSavedConfig, setRecentSavedConfig] = useState<any>(null);
  // Reset atoms
  const resetForecastSettings = useSetAtom(resetForecastSettingsFormAtom);
  const resetSelectedDcaScenario = useSetAtom(resetSelectedDcaScenarioAtom);
  const resetSelectedRecordVersion = useSetAtom(resetSelectedRecordVersionAtom);
  const resetSelectedTypeCurve = useSetAtom(resetSelectedTypeCurveAtom);
  const resetSelectedFitStreams = useSetAtom(resetSelectedFitStreamsAtom);
  const resetModelForm = useSetAtom(resetModelFormAtom);

  // Validation state
  const isOilModelValid = useAtomValue(isOilModelValidAtom);
  const isGasModelValid = useAtomValue(isGasModelValidAtom);
  const isWaterModelValid = useAtomValue(isWaterModelValidAtom);

  // Dirty state
  const isModelFormDirty = useAtomValue(isModelFormDirtyAtom);
  const isOilModelDirty = isModelFormDirty('oil');
  const isGasModelDirty = isModelFormDirty('gas');
  const isWaterModelDirty = isModelFormDirty('water');

  // Get form values to force remount when decline/rampup models change
  const oilFormValues = useAtomValue(oilModelFormValuesAtom);
  const gasFormValues = useAtomValue(gasModelFormValuesAtom);
  const waterFormValues = useAtomValue(waterModelFormValuesAtom);

  // Local reset counters to force component remount on reset
  const [oilResetCounter, setOilResetCounter] = useState(0);
  const [gasResetCounter, setGasResetCounter] = useState(0);
  const [waterResetCounter, setWaterResetCounter] = useState(0);

  const scenarioId = selectedDcaScenario?.id;
  const versionId = selectedRecordVersion?.versionId;

  // Switching streams brings the one being edited into view and puts the other
  // two away, so the panel shows the model the keys are driving. Clicking a
  // heading still opens or closes it by hand.
  const [expandedStreams, setExpandedStreams] = useState<
    Record<HOTKEY_SCOPES, boolean>
  >({
    DCA_INPUT_FORM_OIL: false,
    DCA_INPUT_FORM_GAS: false,
    DCA_INPUT_FORM_WATER: false,
  });

  const activeStreamScope = STREAM_ORDER.find((scope) =>
    activeScopes.includes(scope)
  );

  useEffect(() => {
    if (!activeStreamScope) return;
    setExpandedStreams({
      DCA_INPUT_FORM_OIL: activeStreamScope === 'DCA_INPUT_FORM_OIL',
      DCA_INPUT_FORM_GAS: activeStreamScope === 'DCA_INPUT_FORM_GAS',
      DCA_INPUT_FORM_WATER: activeStreamScope === 'DCA_INPUT_FORM_WATER',
    });
  }, [activeStreamScope]);

  const streamSectionProps = (scope: HOTKEY_SCOPES) => ({
    isHotkeyTarget: activeScopes.includes(scope),
    expanded: expandedStreams[scope],
    onExpandedChange: (open: boolean) =>
      setExpandedStreams((prev) => ({ ...prev, [scope]: open })),
  });

  // Hotkeys mode has one control, here, rather than one per stream. Turning it
  // on selects a stream so the parameter keys have something to drive; Shift
  // with the up and down arrows moves between streams from there.
  const toggleHotkeysMode = () => {
    if (hotkeysMode.active) {
      setHotkeysMode((prev) => ({ ...prev, active: false }));
      STREAM_ORDER.forEach((scope) => disableScope(scope));
      setHelpOpen(false);
      return;
    }

    setHotkeysMode((prev) => ({ ...prev, active: true }));
    if (!activeStreamScope) toggleScope(STREAM_ORDER[0]);
  };

  const handleReset = () => {
    batchedUpdates(() => {
      resetSelectedRecordVersion();
      resetSelectedDcaScenario();
      resetForecastSettings();
      resetModelForm('oil');
      resetModelForm('gas');
      resetModelForm('water');
      // No stream holds a fit's parameters now.
      setFittedProduction(null);
      resetSelectedFitStreams();
      setRecentSavedConfig(null);

      // Increment counters to force remount
      setOilResetCounter((c) => c + 1);
      setGasResetCounter((c) => c + 1);
      setWaterResetCounter((c) => c + 1);
      onReset();
    });
  };

  const payload = useMemo(() => {
    const hasAnyValidModel =
      isOilModelValid || isGasModelValid || isWaterModelValid;

    if (!hasAnyValidModel) {
      return null;
    }

    const production = savedProduction(
      fittedProduction,
      productionSource,
      selectedRecordVersion,
      new Date()
    );

    if (!production || !selectedDcaScenario) {
      return null;
    }

    // if (
    //   earlyTimeStreams.length > 0 &&
    //   (!selectedTypeCurve || !producingWellBore)
    // )
    //   return null;

    const productionSegmentsAttributes: {
      stream: MineralType;
      segmentStartDate: string;
    }[] = [];

    if (segmentationAndOutlierMarkers?.segmentation) {
      Object.entries(segmentationAndOutlierMarkers.segmentation).forEach(
        ([stream, segmentStartDates]) => {
          segmentStartDates.forEach((segmentStartDate) => {
            productionSegmentsAttributes.push({
              stream: stream as MineralType,
              segmentStartDate,
            });
          });
        }
      );
    }

    const productionOutliersAttributes: {
      stream: MineralType;
      outlierDate: string;
    }[] = [];
    if (segmentationAndOutlierMarkers?.outlier) {
      Object.entries(segmentationAndOutlierMarkers.outlier).forEach(
        ([stream, outlierStartDates]) => {
          outlierStartDates.forEach((outlierDate) => {
            productionOutliersAttributes.push({
              stream: stream as MineralType,
              outlierDate,
            });
          });
        }
      );
    }

    const dcaForecastParametersAttributes: {
      inEarlyTime: boolean;
      stream: MineralType;
      dcaParams: Exclude<DeclineCurveAnalysisParams, 'convertToDcaParameters'>;
    }[] = [];

    // Add oil model if valid and dirty
    if (
      isOilModelValid &&
      isOilModelDirty &&
      dcaForecast?.dcaParams.oilDcaParams
    ) {
      dcaForecastParametersAttributes.push({
        inEarlyTime: earlyTimeStreams.includes('oil'),
        stream: 'oil',
        dcaParams: cleanParameters({
          ...dcaForecast.dcaParams.oilDcaParams,
          diFormat: dcaForecast.dcaParams.diFormat,
          forecastDurationOption: dcaForecast.dcaParams.forecastDurationOption,
        }) as DeclineCurveAnalysisParams,
      });
    }

    // Add gas model if valid and dirty
    if (
      isGasModelValid &&
      isGasModelDirty &&
      dcaForecast?.dcaParams.gasDcaParams
    ) {
      dcaForecastParametersAttributes.push({
        inEarlyTime: earlyTimeStreams.includes('gas'),
        stream: 'gas',
        dcaParams: cleanParameters({
          ...dcaForecast.dcaParams.gasDcaParams,
          diFormat: dcaForecast.dcaParams.diFormat,
          forecastDurationOption: dcaForecast.dcaParams.forecastDurationOption,
        }) as DeclineCurveAnalysisParams,
      });
    }

    // Add water model if valid and dirty
    if (
      isWaterModelValid &&
      isWaterModelDirty &&
      dcaForecast?.dcaParams.waterDcaParams
    ) {
      dcaForecastParametersAttributes.push({
        inEarlyTime: earlyTimeStreams.includes('water'),
        stream: 'water',
        dcaParams: cleanParameters({
          ...dcaForecast.dcaParams.waterDcaParams,
          diFormat: dcaForecast.dcaParams.diFormat,
          forecastDurationOption: dcaForecast.dcaParams.forecastDurationOption,
        }) as DeclineCurveAnalysisParams,
      });
    }

    const payload = {
      uwi,
      uwi12: producingWellBore?.uwi12,
      dcaScenarioId: selectedDcaScenario.id,
      productionVolumeVersionId: production.productionVolumeVersionId,
      productionSource: production.productionSource,
      uniqueTcId: selectedTypeCurve?.uniqueTcId,
      notes: '',
      dcaForecastParametersAttributes,
      productionSegmentsAttributes,
      productionOutliersAttributes,
      ...(dcaWorkspaceId !== undefined && { dcaWorkspaceId }),
    };

    const lastSaved = saveAction ? saveAction.savedPayload : recentSavedConfig;

    if (JSON.stringify(payload) === JSON.stringify(lastSaved)) {
      return null;
    }

    return payload;
  }, [
    isOilModelValid,
    isOilModelDirty,
    isGasModelValid,
    isGasModelDirty,
    isWaterModelValid,
    isWaterModelDirty,
    dcaForecast,
    earlyTimeStreams,
    selectedTypeCurve,
    recentSavedConfig,
    producingWellBore,
    selectedRecordVersion,
    productionSource,
    fittedProduction,
    selectedDcaScenario,
    segmentationAndOutlierMarkers,
    uwi,
    dcaWorkspaceId,
    saveAction,
  ]);

  const handleSave = () => {
    if (!payload) return;

    // The payload memo has a production whenever it has a payload. It is
    // worked out again on the click, so a hand edit saved after midnight
    // records the day it was saved, not the day the memo last ran.
    const savedPayload = {
      ...payload,
      ...savedProduction(
        fittedProduction,
        productionSource,
        selectedRecordVersion,
        new Date()
      ),
    };

    if (saveAction) {
      saveAction.onSave(savedPayload as DcaForecastSavePayload);
      return;
    }

    clearDcaNotificationGroup('dca-forecast-save-error');

    setSaveDcaForecasVersion({
      type: 'POST',
      url: '/wells/dca_forecasts',
      data: {
        dcaForecast: savedPayload,
      },
      onSuccess: (savedForecast) => {
        setRecentSavedConfig(savedPayload);
        triggerRefetchInsightForecastVersions();
        onForecastSaved?.();

        if (selectedDcaScenario) {
          const forecastName =
            savedForecast?.businessId ?? `V${totalInsightForecastVersions + 1}`;
          setDcaNotification('dca-forecast-save-success', [
            {
              id: 'dca-forecast-save-success',
              title: 'Success',
              message: `DCA Forecast ${forecastName} was saved under ${selectedDcaScenario.name} DCA Scenario`,
              type: 'success',
              hideCloseButton: false,
            },
          ]);

          setTimeout(() => {
            clearDcaNotificationGroup('dca-forecast-save-success');
          }, 5000);
        }
      },
      onError: (error: any) => {
        let messages: string[];
        if (error?.errors && typeof error.errors === 'object') {
          messages = Object.values(error.errors).map((msg) =>
            (msg as string[]).join(', ')
          );
        } else if (typeof error?.error === 'string') {
          messages = [error.error];
        } else {
          messages = ['Error saving DCA Forecast'];
        }

        setDcaNotification(
          'dca-forecast-save-error',
          messages.map((message, i) => ({
            id: `dca-forecast-save-error-${i}`,
            title: 'Save DCA Forecast',
            message,
            type: 'error',
          }))
        );
      },
    });
  };

  // Ctrl/Cmd+S in hotkeys mode bumps the save-request counter; the payload
  // lives here, so this is where the save actually runs.
  const saveRequest = useAtomValue(dcaSaveRequestAtom);
  const handledSaveRequestRef = useRef(saveRequest);

  useEffect(() => {
    if (saveRequest === handledSaveRequestRef.current) return;
    handledSaveRequestRef.current = saveRequest;
    handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequest]);

  const isDummyScenario = useMemo(() => {
    return DUMMY_DCA_SCENARIOS.some(
      (dummy) => dummy.id === selectedDcaScenario?.id
    );
  }, [selectedDcaScenario?.id]);

  return (
    <div className={style.dcaParamsContainer}>
      <div className={style.configurationPanelHeader}>
        <h3 className={style.configurationPanelTitle}>Configuration Panel</h3>
        <div className={style.configurationPanelActions}>
          {enableHotkeysMode && (
            <Button
              size="sm"
              kind={hotkeysMode.active ? 'primary' : 'ghost'}
              renderIcon={MacCommand}
              iconDescription={
                hotkeysMode.active ? 'Leave hotkeys mode' : 'Enter hotkeys mode'
              }
              hasIconOnly
              tooltipAlignment="end"
              onClick={toggleHotkeysMode}
            />
          )}
          <Button
            size="sm"
            renderIcon={Reset}
            iconDescription="Reset"
            hasIconOnly
            className={style.resetButton}
            onClick={handleReset}
          />
          <Tooltip align="bottom" autoAlign label={saveAction?.label ?? 'Save'}>
            <Button
              size="sm"
              renderIcon={Save}
              iconDescription="Save"
              className={style.saveButton}
              onClick={handleSave}
              disabled={
                !payload ||
                saveDcaForecasVersion.loading ||
                isDummyScenario ||
                !!saveAction?.loading
              }
            >
              Save
            </Button>
          </Tooltip>
        </div>
      </div>
      <FitStreamsSelector
        disabled={disableFitStreams}
        uwi={uwi}
        earlyTimeStreams={earlyTimeStreams}
        producingWellBore={producingWellBore}
      />
      <PanelSection
        title="Production Versions"
        onReset={resetSelectedRecordVersion}
      >
        <ProductionVersionSelector
          recordVersionHref={recordVersionHref}
          dailyRecordVersionHref={dailyRecordVersionHref}
          productionDataSources={productionDataSources}
          uwi={uwi}
        />
      </PanelSection>
      <PanelSection title="DCA Scenarios" onReset={resetSelectedDcaScenario}>
        <DcaScenarioSelector />
      </PanelSection>
      <PanelSection title="PDP Forecast Versions" onReset={() => {}}>
        <PdpForecastVersionSelector
          uwi={uwi}
          legacyPdpForecastVersionsHref={legacyPdpForecastVersionsHref}
          insightsPdpForecastVersionsHref={insightsPdpForecastVersionsHref}
          onApply={onApplyPdpForecastVersion}
          onSelectedVersionsChange={onSelectedPdpVersionsChange}
          includeAllVersions={dcaWorkspaceId !== undefined}
        />
      </PanelSection>
      {/* Forecast Settings Section */}
      <PanelSection title="Forecast Settings" onReset={resetForecastSettings}>
        <ForecastSettings />
      </PanelSection>
      <PanelSection
        title="OIL Model"
        onReset={() => {
          resetModelForm('oil');
          setOilResetCounter((c) => c + 1);
        }}
        {...streamSectionProps('DCA_INPUT_FORM_OIL')}
      >
        <VolumeModel
          key={`oil-${scenarioId}-${oilFormValues.declineModel.value}-${oilFormValues.rampupModel.value}-${oilResetCounter}`}
          streamType="oil"
          formAtoms={oilModelFormAtom}
          versionId={versionId ?? ''}
          uwi={uwi}
        />
      </PanelSection>
      <PanelSection
        title="GAS Model"
        onReset={() => {
          resetModelForm('gas');
          setGasResetCounter((c) => c + 1);
        }}
        {...streamSectionProps('DCA_INPUT_FORM_GAS')}
      >
        <VolumeModel
          key={`gas-${scenarioId}-${gasFormValues.declineModel.value}-${gasFormValues.rampupModel.value}-${gasResetCounter}`}
          streamType="gas"
          formAtoms={gasModelFormAtom}
          versionId={versionId ?? ''}
          uwi={uwi}
        />
      </PanelSection>
      <PanelSection
        title="WATER Model"
        onReset={() => {
          resetModelForm('water');
          setWaterResetCounter((c) => c + 1);
        }}
        {...streamSectionProps('DCA_INPUT_FORM_WATER')}
      >
        <VolumeModel
          key={`water-${scenarioId}-${waterFormValues.declineModel.value}-${waterFormValues.rampupModel.value}-${waterResetCounter}`}
          streamType="water"
          formAtoms={waterModelFormAtom}
          versionId={versionId ?? ''}
          uwi={uwi}
        />
      </PanelSection>
      <PanelSection
        title="Type Curves"
        onReset={() => resetSelectedTypeCurve(uwi)}
      >
        <TypeCurveSelector uwi={uwi} />
      </PanelSection>
    </div>
  );
};

const PanelSection = ({
  title,
  children,
  onReset,
  isHotkeyTarget = false,
  expanded,
  onExpandedChange,
}: PanelSectionProps) => {
  return (
    <div
      className={isHotkeyTarget ? style.activeHotkey : undefined}
      data-testid={isHotkeyTarget ? 'active-hotkey' : undefined}
    >
      <Accordion expanded={expanded} onClick={onExpandedChange}>
        <AccordionSummary className={style.accordionHeader}>
          <div className={style.accordionCustomHeader}>
            <span>{title}</span>
            <div className={style.actionContainer}>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReset();
                }}
                className={style.resetIcon}
              >
                <Reset />
              </span>
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={style.accordionContent}>{children}</div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const cleanParameters = (params: Record<string, any>): any => {
  return Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === 'object') {
          acc[key] = cleanParameters(value);
        } else if (isNaN(value)) {
          acc[key] = value;
        } else {
          acc[key] = Number(value);
        }
      }
      return acc;
    },
    {} as Record<string, any>
  );
};
